import { Paper, Stack, Typography } from '@mui/material';
import { parseCallSummary } from '@/utils/calls';
import { formatSecondsToTime } from '@/utils/formatting';
import InfoItem from '@/components/common/InfoItem';
import CallKeyInfo from './CallKeyInfo';
import PhrasesList from './PhrasesList';
import { getOpeningPhrases, getClosingPhrases, mapCallEndingReasonToLabel } from './callDetailsUtils';

import type { CallLogsMeta } from '@/types/calls';
import type { CombinedTranscript } from '@/types/transcript';

interface CallDetailsProps {
    transcript?: CombinedTranscript[] | null;
    talkTime?: number | null;
    callEndingReason?: string | null;
    metadata?: CallLogsMeta[];
    agentSpeechProportion?: number | null;
    qualityScore?: number | null;
}

/**
 * Displays call details on the call page.
 *
 * @param {CallDetailsProps} props
 * @returns {JSX.Element}
 */
const CallDetails: React.FC<CallDetailsProps> = (props) => {
    const { transcript, talkTime = 0, callEndingReason = null, metadata, agentSpeechProportion = null, qualityScore = null } = props;

    const parsedSummary = parseCallSummary(metadata);

    const duration = formatSecondsToTime(talkTime || 0);
    const summaryData = parsedSummary?.summary;
    const summaryText = summaryData?.summary || 'N/A';
    const endingReasonLabel = mapCallEndingReasonToLabel(callEndingReason || null);

    const transcriptData = transcript as CombinedTranscript[] | null;
    const openingPhrases = getOpeningPhrases({ transcript: transcriptData, count: 2 });
    const closingPhrases = getClosingPhrases({ transcript: transcriptData, count: 2 });

    return (
        <Paper data-testid="call-details" sx={{ p: 2 }}>
            <CallKeyInfo
                duration={duration}
                endingReason={endingReasonLabel}
                agentSpeechProportion={agentSpeechProportion}
                qualityScore={qualityScore}
            />
            <Stack spacing={1}>
                <InfoItem label="Call summary" labelId="call-summary-label" data-testid="call-summary">
                    <Typography variant="body2" sx={{ lineHeight: 1.6, wordBreak: 'break-word' }} aria-labelledby="call-summary">
                        {summaryText}
                    </Typography>
                </InfoItem>
                {openingPhrases.length > 0 && (
                    <InfoItem label="Agent's opening phrases" labelId="call-opening-label">
                        <PhrasesList phrases={openingPhrases} emptyMessage="N/A" labelId="call-opening-phrases" />
                    </InfoItem>
                )}
                {closingPhrases.length > 0 && (
                    <InfoItem label="Agent's closing phrases" labelId="call-closing-label" data-testid="call-closing-phrases">
                        <PhrasesList phrases={closingPhrases} emptyMessage="N/A" labelId="call-closing-phrases" />
                    </InfoItem>
                )}
            </Stack>
        </Paper>
    );
};

export default CallDetails;
